'use client'

import { useEffect, useState, useRef } from 'react'
import ReassuranceTestimonialsCard from '../ReassuranceTestimonialsCard/ReassuranceTestimonialsCard'
import { Button, cn, LeftArrow, RightArrow } from '@desoutter/uikit'
import { ReassuranceTestimonialsCarouselFragment } from './ReassuranceTestimonialsCarousel.generated'

type ReassuranceTestimonialsCarouselProps = {
	fragment: ReassuranceTestimonialsCarouselFragment
	className?: string
	autoplay?: boolean
	interval?: number
}

export default function ReassuranceTestimonialsCarousel({
	fragment,
	className,
	autoplay = true,
	interval = 5000,
}: ReassuranceTestimonialsCarouselProps) {
	const [activeTestimonyIndex, setActiveTestimonyIndex] = useState(0)
	const [activeTestimony, setActiveTestimony] = useState(
		fragment.testimonies[activeTestimonyIndex],
	)
	const [isPaused, setIsPaused] = useState(false)
	const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		setActiveTestimony(
			fragment.testimonies[activeTestimonyIndex % fragment.testimonies.length],
		)
	}, [activeTestimonyIndex, fragment.testimonies])

	// autoplay
	useEffect(() => {
		const startAutoplay = () => {
			if (autoplay && fragment.testimonies.length > 1 && !isPaused) {
				autoplayTimerRef.current = setInterval(() => {
					setActiveTestimonyIndex(
						(prevIndex) => (prevIndex + 1) % fragment.testimonies.length,
					)
				}, interval)
			}
		}

		const stopAutoplay = () => {
			if (autoplayTimerRef.current) {
				clearInterval(autoplayTimerRef.current)
				autoplayTimerRef.current = null
			}
		}

		stopAutoplay()
		startAutoplay()

		return () => stopAutoplay()
	}, [autoplay, interval, isPaused, fragment.testimonies.length])

	// Handlers pour les interactions utilisateur
	const handleNavigation = (direction: 'prev' | 'next') => {
		// pause autoplay
		setIsPaused(true)
		if (direction === 'prev') {
			setActiveTestimonyIndex((prevIndex) =>
				prevIndex === 0 ? fragment.testimonies.length - 1 : prevIndex - 1,
			)
		} else {
			setActiveTestimonyIndex(
				(prevIndex) => (prevIndex + 1) % fragment.testimonies.length,
			)
		}

		//restore autoplay
		setTimeout(() => setIsPaused(false), interval * 2)
	}

	const handleTestimonySelect = (testimony: any) => {
		setIsPaused(true)
		setActiveTestimony(testimony)
		const newIndex = fragment.testimonies.findIndex(
			(t) => t.id === testimony.id,
		)
		if (newIndex !== -1) {
			setActiveTestimonyIndex(newIndex)
		}

		// restore autoplay
		setTimeout(() => setIsPaused(false), interval * 2)
	}

	// stop autoplay when mouse is over the carousel
	const handleMouseEnter = () => setIsPaused(true)
	const handleMouseLeave = () => setIsPaused(false)

	return (
		<div
			className={cn('flex flex-col w-full gap-4', className)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<div className="flex w-full items-center">
				{fragment.testimonies.length > 1 && (
					<Button
						className="bg-gray-50 dark:bg-gray-800 hover:scale-110 transition-all duration-300"
						color="tertiary"
						title="backward"
						icon={LeftArrow}
						onClick={() => handleNavigation('prev')}
					/>
				)}
				<ReassuranceTestimonialsCard
					fragment={activeTestimony}
					isDark={fragment.isDark}
					showAuthor={fragment.showAuthors}
				/>
				{fragment.testimonies.length > 1 && (
					<Button
						className="bg-gray-50 dark:bg-gray-800 hover:scale-110 transition-all duration-300"
						color="tertiary"
						title="forward"
						icon={RightArrow}
						onClick={() => handleNavigation('next')}
					/>
				)}
			</div>

			{fragment.testimonies.length > 1 && (
				<div className="flex w-full gap-2 h-1.5">
					{fragment.testimonies.map((testimony) => (
						<div
							key={testimony.id}
							className={cn(
								'h-full w-full cursor-pointer transition-colors duration-1000',
								activeTestimony.id === testimony.id
									? 'bg-primary text-primary-foreground'
									: 'bg-gray-300 dark:bg-gray-700',
							)}
							onClick={() => handleTestimonySelect(testimony)}
							role="button"
						></div>
					))}
				</div>
			)}
		</div>
	)
}
